import { CapturedEmoji, EmojiGameConfig, Point, Position, WindowSize, EmojiGameLevel, EmojiTheme } from '../utils/types';
import { calculateBeePosition } from './beePathUtils';
import { getRandomEmojiFromTheme } from '../utils/emojiThemes';
import { superEmojiService } from './superEmojiService';

class EmojiRescueService {
  private static instance: EmojiRescueService;

  private constructor() {}

  static getInstance(): EmojiRescueService {
    if (!EmojiRescueService.instance) {
      EmojiRescueService.instance = new EmojiRescueService();
    }
    return EmojiRescueService.instance;
  }

  calculateGameConfig(emojiCount: number, flyingTime: number, theme: EmojiTheme): EmojiGameConfig {
    return {
      beeCount: emojiCount, // Keep for backward compatibility
      emojiCount,
      totalDuration: flyingTime,
      maxTaps: 1000, // Default max taps for all levels
      theme,
      totalEmojisForLevel: emojiCount, // For user-generated paths, total = concurrent
      maxConcurrentEmojis: emojiCount
    };
  }

  initializeCapturedEmojis(
    paths: Point[][],
    startTime: number,
    isAutoMode: boolean,
    theme: EmojiTheme
  ): CapturedEmoji[] {
    if (!isAutoMode) {
      // Single emoji for user-generated path
      const initialPosition = paths[0][0];
      return [{
        id: 'emoji-0',
        position: { 
          x: initialPosition.x, 
          y: initialPosition.y, 
          rotation: initialPosition.rotation 
        },
        path: paths[0],
        startTime,
        status: 'captured',
        isActive: true,
        ...(() => {
          const { emoji, isSuperEmoji } = this.getEmojiForSpawn(theme);
          return { emoji, isSuperEmoji };
        })(),
        theme: theme.name,
        ufoType: theme.ufoStyle
      }];
    }

    // Multiple emojis for auto mode
    return paths.map((path, index) => {
      const initialPosition = path[0];
      return {
        id: `emoji-${index}`,
        position: { 
          x: initialPosition.x, 
          y: initialPosition.y, 
          rotation: initialPosition.rotation 
        },
        path,
        startTime: startTime + (index * 1000), // Staggered start
        status: 'captured',
        isActive: true,
        ...(() => {
          const { emoji, isSuperEmoji } = this.getEmojiForSpawn(theme);
          return { emoji, isSuperEmoji };
        })(),
        theme: theme.name,
        ufoType: theme.ufoStyle
      };
    });
  }

  initializeCapturedEmojisWithIntervals(
    paths: Point[][],
    startTime: number,
    levelConfig: EmojiGameLevel
  ): CapturedEmoji[] {
    return paths.map((path, index) => {
      const initialPosition = path[0];
      return {
        id: `emoji-${index}`,
        position: { 
          x: initialPosition.x, 
          y: initialPosition.y, 
          rotation: initialPosition.rotation 
        },
        path,
        startTime: startTime + (index * levelConfig.intervalBetweenEmojis),
        status: 'captured',
        isActive: true,
        ...(() => {
          const { emoji, isSuperEmoji } = this.getEmojiForSpawn(levelConfig.theme);
          return { emoji, isSuperEmoji };
        })(),
        theme: levelConfig.theme.name,
        ufoType: levelConfig.theme.ufoStyle
      };
    });
  }

  updateEmojiPositions(
    emojis: CapturedEmoji[],
    currentTime: number,
    windowSize: WindowSize
  ): CapturedEmoji[] {
    return emojis.map(emoji => {
      if (!emoji.isActive) return emoji;

      const emojiElapsed = currentTime - emoji.startTime;
      const totalDuration = emoji.path[emoji.path.length - 1].time;

      if (emoji.status === 'falling') {
        // Simulate falling after rescue
        const newY = emoji.position.y + 12; // Slightly faster fall than original
        if (newY > windowSize.height) {
          // Emoji has fallen off-screen after rescue
          return { ...emoji, isActive: false, status: 'rescued', rescuedTime: currentTime };
        }
        return { ...emoji, position: { ...emoji.position, y: newY } };
      }

      if (emojiElapsed >= totalDuration) {
        // Emoji reached end of path without being rescued
        return { ...emoji, isActive: false, status: 'escaped' };
      }

      const position = calculateBeePosition(
        emoji.path,
        emojiElapsed,
        totalDuration,
        windowSize.width,
        windowSize.height
      );

      return { ...emoji, position };
    });
  }

  handleEmojiTap(emojis: CapturedEmoji[], emojiId: string): CapturedEmoji[] {
    return emojis.map(emoji => {
      if (emoji.id === emojiId && emoji.status === 'captured') {
        return { ...emoji, status: 'falling', isActive: true };
      }
      return emoji;
    });
  }

  getGameStatus(emojis: CapturedEmoji[], remainingTaps: number, totalEmojisForLevel?: number, rescuedCount?: number): 'playing' | 'won' | 'lost' {
    let status: 'playing' | 'won' | 'lost' = 'playing';

    // Priority 1: If any emoji 'escaped' (reached end of path without rescue), it's a loss
    if (emojis.some(emoji => emoji.status === 'escaped')) {
      status = 'lost';
    } else if (totalEmojisForLevel && rescuedCount !== undefined) {
      // NEW: For continuous spawning levels, check if we've rescued the total target count
      if (rescuedCount >= totalEmojisForLevel) {
        status = 'won';
      } else if (emojis.some(emoji => emoji.status === 'captured' || emoji.status === 'falling')) {
        // Still playing if there are active emojis and haven't reached target
        status = 'playing';
      } else {
        // No active emojis but haven't reached target - keep playing (more will spawn)
        status = 'playing';
      }
    } else if (emojis.every(emoji => emoji.status === 'rescued')) {
      // Priority 2: Legacy logic - If all emojis are 'rescued' (hit and fell off screen)
      status = 'won';
    } else if (emojis.some(emoji => emoji.status === 'captured' || emoji.status === 'falling')) {
      // Default: If there are still 'captured' or 'falling' emojis, the game is still playing
      status = 'playing';
    }
    return status;
  }

  calculateProgress(
    currentTime: number,
    gameStartTime: number,
    totalDuration: number
  ): number {
    const elapsed = currentTime - gameStartTime;
    return Math.min(elapsed / totalDuration, 1);
  }

  // Helper method to get rescued emojis for collection tracking
  getRescuedEmojis(emojis: CapturedEmoji[]): string[] {
    return emojis
      .filter(emoji => emoji.status === 'rescued')
      .map(emoji => emoji.emoji);
  }

  // Helper method to count emojis by status
  getEmojiCountByStatus(emojis: CapturedEmoji[], status: 'captured' | 'rescued' | 'escaped' | 'falling'): number {
    return emojis.filter(emoji => emoji.status === status).length;
  }

  // Helper method to check if an emoji is expiring soon (within 2 seconds)
  isEmojiExpiringSoon(emoji: CapturedEmoji, currentTime: number): boolean {
    if (emoji.status !== 'captured' || !emoji.isActive) {
      return false;
    }

    const emojiElapsed = currentTime - emoji.startTime;
    const totalDuration = emoji.path[emoji.path.length - 1].time;
    const timeRemaining = totalDuration - emojiElapsed;
    
    // Return true if less than 2 seconds remaining
    return timeRemaining <= 2000 && timeRemaining > 0;
  }

  // NEW: Method to spawn a new emoji at runtime
  async spawnNewEmoji(
    theme: EmojiTheme,
    startTime: number,
    emojiIndex: number,
    windowSize: { width: number; height: number }
  ): Promise<CapturedEmoji> {
    // Generate a new path for this emoji
    const { gameService } = await import('./gameService');
    const path = await gameService.generatePath(windowSize);
    
    const initialPosition = path[0];
    const uniqueId = `emoji-spawned-${emojiIndex}-${startTime}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      id: uniqueId,
      position: { 
        x: initialPosition.x, 
        y: initialPosition.y, 
        rotation: initialPosition.rotation 
      },
      path,
      startTime,
      status: 'captured',
      isActive: true,
      ...(() => {
        const { emoji, isSuperEmoji } = this.getEmojiForSpawn(theme);
        return { emoji, isSuperEmoji };
      })(),
      theme: theme.name,
      ufoType: theme.ufoStyle
    };
  }

  // BACKWARD COMPATIBILITY METHODS (to ease transition)
  // These methods maintain the original bee naming but delegate to emoji methods

  initializeBees(
    paths: Point[][],
    startTime: number,
    isAutoMode: boolean,
    theme?: EmojiTheme
  ): CapturedEmoji[] {
    const defaultTheme: EmojiTheme = {
      name: "Barnyard Rescue",
      emojis: ["🐄", "🐷", "🐔", "🐑"],
      backgroundColor: "#4CAF50",
      ufoStyle: "basic",
      description: "Save the farm animals!",
      unlockLevel: 1
    };
    return this.initializeCapturedEmojis(paths, startTime, isAutoMode, theme || defaultTheme);
  }

  initializeBeesWithIntervals(
    paths: Point[][],
    startTime: number,
    levelConfig: any // Accept both GameLevel and EmojiGameLevel
  ): CapturedEmoji[] {
    // If it's an EmojiGameLevel, use it directly
    if (levelConfig.theme) {
      return this.initializeCapturedEmojisWithIntervals(paths, startTime, levelConfig);
    }
    
    // Otherwise, create a default emoji level config
    const defaultTheme: EmojiTheme = {
      name: "Barnyard Rescue",
      emojis: ["🐄", "🐷", "🐔", "🐑"],
      backgroundColor: "#4CAF50",
      ufoStyle: "basic",
      description: "Save the farm animals!",
      unlockLevel: 1
    };
    
    const emojiLevelConfig: EmojiGameLevel = {
      ...levelConfig,
      theme: defaultTheme,
      emojiCount: levelConfig.beeCount,
      intervalBetweenEmojis: levelConfig.intervalBetweenBees
    };
    
    return this.initializeCapturedEmojisWithIntervals(paths, startTime, emojiLevelConfig);
  }

  updateBeePositions(
    bees: CapturedEmoji[],
    currentTime: number,
    windowSize: WindowSize
  ): CapturedEmoji[] {
    return this.updateEmojiPositions(bees, currentTime, windowSize);
  }

  handleBeeTap(bees: CapturedEmoji[], beeId: string): CapturedEmoji[] {
    return this.handleEmojiTap(bees, beeId);
  }

  // Super emoji helper methods
  private shouldSpawnSuperEmoji(): boolean {
    return superEmojiService.shouldSpawnSuperEmoji();
  }

  private getEmojiForSpawn(theme: EmojiTheme): { emoji: string; isSuperEmoji: boolean } {
    const isSuperEmoji = this.shouldSpawnSuperEmoji();
    if (isSuperEmoji) {
      const superEmoji = superEmojiService.getRandomSuperEmoji();
      console.log(`🌟 Spawning super emoji: ${superEmoji}`);
      return {
        emoji: superEmoji,
        isSuperEmoji: true
      };
    }
    return {
      emoji: getRandomEmojiFromTheme(theme),
      isSuperEmoji: false
    };
  }
}

export const emojiRescueService = EmojiRescueService.getInstance(); 

// Also export as beeService for backward compatibility during transition
export const beeService = emojiRescueService; 